import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { myobExportApi, type MyobExportRun } from "@/lib/myob-export";
import { ErrorState } from "@/components/error-state";
import { LoadingState } from "@/components/loading-state";
import { EmptyState } from "@/components/empty-state";
import { useConfirm } from "@/hooks/use-confirm";
import {
  Table,
  TableHeader,
  TableBody,
  TableHead,
  TableRow,
  TableCell,
} from "@/components/ui/table";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { formatDate, formatDateTime } from "@/lib/format";

import { PageHeader } from "@/components/page-header";

function fmt(n: number) {
  return n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

export function MyobExportPage() {
  const qc = useQueryClient();
  const confirm = useConfirm();

  const preview = useQuery({ queryKey: ["myob-preview"], queryFn: myobExportApi.preview });
  const runs = useQuery({ queryKey: ["myob-runs"], queryFn: () => myobExportApi.runs(50) });

  const generate = useMutation({
    mutationFn: myobExportApi.generate,
    onSuccess: async (run) => {
      if (run.status === "Succeeded" && run.fileName) {
        await myobExportApi.download(run.myobExportRunId, run.fileName);
      }
      await qc.invalidateQueries({ queryKey: ["myob-preview"] });
      await qc.invalidateQueries({ queryKey: ["myob-runs"] });
    },
  });

  return (
    <div className="max-w-6xl mx-auto space-y-6 py-6 px-4">
      <PageHeader title="MYOB Export" />

      {/* Preview card */}
      {preview.isLoading ? (
        <Card className="border border-slate-200 shadow-sm rounded-xl overflow-hidden bg-white">
          <LoadingState />
        </Card>
      ) : preview.isError ? (
        <Card className="border border-slate-200 shadow-sm rounded-xl overflow-hidden bg-white p-6">
          <ErrorState onRetry={() => void preview.refetch()} />
        </Card>
      ) : (
        <Card className="border border-slate-200 shadow-sm rounded-xl overflow-hidden bg-white">
          <CardHeader className="border-b border-slate-100 bg-slate-50/50 py-3 px-4">
            <CardTitle role="heading" aria-level={3} className="text-xs font-bold text-slate-800 uppercase tracking-wider">
              Ready to export
            </CardTitle>
          </CardHeader>
          <CardContent className="p-6">
            {preview.data && preview.data.hasAnything ? (
              <div className="space-y-6">
                <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                  <div className="bg-[#0f3453]/5 border border-[#0f3453]/10 p-4 rounded-xl text-center">
                    <div className="text-[10px] text-slate-500 font-bold uppercase tracking-wider">Invoices</div>
                    <div className="text-2xl font-extrabold text-slate-900 mt-1">{preview.data.invoiceCount}</div>
                  </div>
                  <div className="bg-[#0f3453]/5 border border-[#0f3453]/10 p-4 rounded-xl text-center">
                    <div className="text-[10px] text-slate-500 font-bold uppercase tracking-wider">Payments</div>
                    <div className="text-2xl font-extrabold text-slate-900 mt-1">{preview.data.paymentCount}</div>
                  </div>
                  <div className="bg-[#0f3453]/5 border border-[#0f3453]/10 p-4 rounded-xl text-center">
                    <div className="text-[10px] text-slate-500 font-bold uppercase tracking-wider">Total Debits</div>
                    <div className="text-2xl font-extrabold text-slate-900 mt-1">${fmt(preview.data.totalDebits)}</div>
                  </div>
                  <div className="bg-[#0f3453]/5 border border-[#0f3453]/10 p-4 rounded-xl text-center">
                    <div className="text-[10px] text-slate-500 font-bold uppercase tracking-wider">Total Credits</div>
                    <div className="text-2xl font-extrabold text-slate-900 mt-1">${fmt(preview.data.totalCredits)}</div>
                  </div>
                  <div className="bg-[#0f3453]/5 border border-[#0f3453]/10 p-4 rounded-xl text-center col-span-2 md:col-span-1 flex flex-col justify-center">
                    <div className="text-[10px] text-slate-500 font-bold uppercase tracking-wider">Up To</div>
                    <div className="text-sm font-bold text-slate-900 mt-1">
                      {preview.data.cutoffAccountingDate ? formatDate(preview.data.cutoffAccountingDate) : "—"}
                    </div>
                  </div>
                </div>

                <div className="flex justify-end pt-4 border-t border-slate-100">
                  <Button
                    onClick={() => {
                      void (async () => {
                        const ok = await confirm({
                          title: "Generate MYOB file?",
                          body: `Generate a MYOB file for ${preview.data!.invoiceCount} invoice(s) and ${preview.data!.paymentCount} payment(s)? These will be marked as exported.`,
                          confirmLabel: "Confirm & download",
                          intent: "primary",
                        });
                        if (ok) generate.mutate();
                      })();
                    }}
                    disabled={generate.isPending}
                    className="bg-[#0f3453] text-white hover:bg-[#1a2f4c] shadow-sm font-semibold h-9 px-4 rounded-md"
                  >
                    {generate.isPending ? "Generating…" : "Generate MYOB file"}
                  </Button>
                </div>
                {generate.isError && (
                  <p className="text-sm text-destructive text-right mt-2">Export failed. Please try again.</p>
                )}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground text-center py-4">Nothing new to export.</p>
            )}
          </CardContent>
        </Card>
      )}

      {/* History Card */}
      <Card className="border border-slate-200 shadow-sm rounded-xl overflow-hidden bg-white">
        <CardHeader className="border-b border-slate-100 bg-slate-50/50 py-3 px-4">
          <CardTitle role="heading" aria-level={3} className="text-xs font-bold text-slate-800 uppercase tracking-wider">
            History
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {runs.isLoading ? (
            <div className="p-6">
              <LoadingState />
            </div>
          ) : runs.data?.length === 0 ? (
            <div className="p-8">
              <EmptyState headline="No exports yet." />
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader className="bg-slate-50 border-b border-slate-200">
                  <TableRow>
                    <TableHead className="text-[10px] font-bold text-slate-500 uppercase tracking-wider h-10 px-4">Generated</TableHead>
                    <TableHead className="text-[10px] font-bold text-slate-500 uppercase tracking-wider h-10 px-4">By</TableHead>
                    <TableHead className="text-[10px] font-bold text-slate-500 uppercase tracking-wider h-10 px-4">Invoices</TableHead>
                    <TableHead className="text-[10px] font-bold text-slate-500 uppercase tracking-wider h-10 px-4">Payments</TableHead>
                    <TableHead className="text-[10px] font-bold text-slate-500 uppercase tracking-wider h-10 px-4">Debits</TableHead>
                    <TableHead className="text-[10px] font-bold text-slate-500 uppercase tracking-wider h-10 px-4">Credits</TableHead>
                    <TableHead className="text-[10px] font-bold text-slate-500 uppercase tracking-wider h-10 px-4">Status</TableHead>
                    <TableHead className="text-[10px] font-bold text-slate-500 uppercase tracking-wider h-10 px-4"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {(runs.data ?? []).map((r: MyobExportRun) => (
                    <TableRow key={r.myobExportRunId} className="border-b border-slate-100 hover:bg-slate-50/50">
                      <TableCell className="text-xs text-slate-600 px-4 py-3">{formatDateTime(r.generatedAtUtc)}</TableCell>
                      <TableCell className="text-xs text-slate-600 px-4 py-3">{r.generatedByName}</TableCell>
                      <TableCell className="text-xs text-slate-600 px-4 py-3">{r.invoiceCount}</TableCell>
                      <TableCell className="text-xs text-slate-600 px-4 py-3">{r.paymentCount}</TableCell>
                      <TableCell className="text-xs text-slate-600 px-4 py-3">${fmt(r.totalDebits)}</TableCell>
                      <TableCell className="text-xs text-slate-600 px-4 py-3">${fmt(r.totalCredits)}</TableCell>
                      <TableCell className="text-xs text-slate-600 px-4 py-3">
                        <span className={`inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-full border ${
                          r.status === "Succeeded"
                            ? "text-emerald-600 bg-emerald-50 border-emerald-200"
                            : "text-red-600 bg-red-50 border-red-200"
                        }`}>
                          {r.status}
                        </span>
                      </TableCell>
                      <TableCell className="text-xs px-4 py-3 text-right">
                        {r.status === "Succeeded" && r.fileName ? (
                          <button
                            className="text-blue-600 hover:text-blue-800 hover:underline font-medium"
                            onClick={() => void myobExportApi.download(r.myobExportRunId, r.fileName!)}
                          >
                            Re-download
                          </button>
                        ) : null}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
